import React, { Fragment } from "react"

import { useTranslation } from "react-i18next"

import { Box } from "@mui/material"
import Grid from "@mui/material/Grid"
import Skeleton from "@mui/material/Skeleton"
import type { BarDatum } from "@nivo/bar"
import { ResponsiveBar } from "@nivo/bar"
import { Typography } from "@synapse-analytics/synapse-ui"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import type { ModelAverageCpuUsage } from "../../../types/custom/projects"
import { customRound } from "../../../utils/genericHelpers"
import { orderModelsByStatus } from "../../../utils/searchSortFilterHelpers"
import { GraphHeader } from "../../graphs/GraphHeader"

type Props = {
  title?: string
  data?: ModelAverageCpuUsage[]
  graphHeight: number
  isLoading: boolean
  isRamUsage?: boolean
  range?: number
}

export function UsageBarChart(props: Props): React.ReactElement {
  const { t } = useTranslation("graphs")
  const { title, data, graphHeight, isLoading, isRamUsage, range } = props

  const theme = getTheme()

  const maxLabelLength = 24

  const adjustedData: ModelAverageCpuUsage[] = React.useMemo(() => {
    if (data && data.length > 0) {
      let newData = Array.from(data)

      newData = newData.filter((item) => {
        return item.average > 0
      })

      return orderModelsByStatus(newData, "desc")
    }
    return []
  }, [data])

  return (
    <div className={"charts-paper"}>
      <GraphHeader
        title={title}
        tooltipText={isRamUsage ? t("tooltips.usageChart.ram") : t("tooltips.usageChart.cpu")}
        range={range}
        padding="1rem"
      />

      <Box style={{ height: graphHeight, padding: "4px 16px 8px 16px" }}>
        {(!data || data.length < 1) && !isLoading ? (
          <Grid container direction="row" justifyContent="center" alignItems="center" className={"full-height"}>
            <Typography variant="p" variantColor={2}>
              {t("barCharts.emptyState.noDataToDisplay")}
            </Typography>
          </Grid>
        ) : isLoading || !data ? (
          <Grid container direction="column" justifyContent="center" alignItems="flex-start" className={"full-height"}>
            <Skeleton animation="wave" width="100%" height={40} />
            <Skeleton animation="wave" width="75%" />
            <Skeleton animation="wave" width="60%" height={30} />
            <Skeleton animation="wave" width="30%" />
          </Grid>
        ) : (
          <ResponsiveBar
            data={adjustedData as BarDatum[]}
            theme={NivoTheme(theme)}
            indexBy="name"
            keys={["average", "total", "max"]}
            colors={[theme.palette.blue.background[1], theme.palette.base.border[1], theme.palette.blue.background[2]]}
            colorBy="id"
            margin={{ top: 30, right: 0, bottom: -20, left: 0 }}
            padding={0.75}
            layout="horizontal"
            valueScale={{ type: "linear" }}
            maxValue={isRamUsage ? 4 : 100}
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={null}
            // valueFormat={(v) => v.toFixed(0)}
            enableLabel={false}
            enableGridY={false}
            labelSkipHeight={12}
            labelTextColor={{ from: "color", modifiers: [["brighter", 3]] }}
            animate={true}
            legends={[
              {
                dataFrom: "keys",
                anchor: "top-left",
                direction: "row",
                justify: false,
                translateX: 0,
                translateY: -22,
                itemsSpacing: 5,
                itemWidth: 100,
                itemHeight: 20,
                itemDirection: "left-to-right",
                itemOpacity: 0.85,
                symbolSize: 15,
                effects: [
                  {
                    on: "hover",
                    style: {
                      itemOpacity: 1,
                    },
                  },
                ],
              },
            ]}
            legendLabel={(v) => t(`usageChart.chartLabels.${v.id as string}`).toUpperCase()}
            labelSkipWidth={2000} // labelSkipWidth = minimum bar size needed for a normal label
            layers={[
              "grid",
              "axes",
              "bars",
              "markers",
              "legends",
              "annotations",
              ({ bars }) => (
                <g>
                  {bars.map(({ width, height, x, y, key, data }) =>
                    width ? (
                      <Fragment key={key}>
                        {/* Model name on bar */}
                        {data.id === "total" ? (
                          <text
                            //Calculate position of model name
                            transform={`translate(${0}, ${y - 9})`}
                            textAnchor="start"
                            dominantBaseline="central"
                            fill={theme.palette.base.text[2]}
                            style={{
                              fontFamily: '"Inter", sans-serif',
                              fontStyle: "normal",
                              fontWeight: 600,
                              fontSize: 12,
                              lineHeight: "16px",
                            }}
                          >
                            {`${
                              key.slice(key.indexOf(".") + 1)
                                ? //truncate model name if it's too long
                                  key.slice(key.indexOf(".") + 1).length > maxLabelLength
                                  ? key.slice(key.indexOf(".") + 1).substring(0, maxLabelLength) + "..."
                                  : key.slice(key.indexOf(".") + 1)
                                : ""
                            }`}
                          </text>
                        ) : (
                          ""
                        )}

                        {!data.value ? (
                          ""
                        ) : //Right text on bar
                        data.id === "total" ? (
                          <text
                            transform={`translate(${width - (isRamUsage ? 40 : 50)}, ${y - 9})`}
                            textAnchor="end"
                            dominantBaseline="central"
                            fill={theme.palette.base.text[2]}
                            style={{
                              fontFamily: '"Inter", sans-serif',
                              fontStyle: "normal",
                              fontWeight: 600,
                              fontSize: 12,
                              lineHeight: "16px",
                            }}
                          >
                            {data.value
                              ? isRamUsage
                                ? t("usageChart.labels.fourGB")
                                : t("usageChart.labels.oneCore")
                              : ""}
                          </text>
                        ) : (
                          ""
                        )}
                        {/* add max marker */}
                        <rect
                          x={(Number(data.data.realMax) / Number(data.data.average)) * x}
                          y={y}
                          width="5"
                          height={data.id === "total" ? height : 0}
                          fill="#B4DCF0"
                        />
                      </Fragment>
                    ) : null,
                  )}
                </g>
              ),
            ]}
            tooltip={(slice) => (
              <div className={"chart-tooltip"}>
                <Typography variant="h3-bold" variantColor={2}>
                  {slice.data.name}
                </Typography>
                <Typography variant="p" variantColor={2}>
                  {isRamUsage
                    ? `${t("usageChart.tooltip.average")} ${slice.data.average ? customRound(Number(slice.data.average) * 1024, 2) : 0}${t("usageChart.units.megabytes")}`
                    : `${t("usageChart.tooltip.average")} ${slice.data.average ? customRound(Number(slice.data.average), 2) : 0}${t("usageChart.units.percentage")}`}
                </Typography>
                <Typography variant="p" variantColor={2}>
                  {isRamUsage
                    ? `${t("usageChart.tooltip.max")} ${slice.data.realMax ? customRound(Number(slice.data.realMax) * 1024, 2) : 0}${t("usageChart.units.megabytes")}`
                    : `${t("usageChart.tooltip.max")} ${slice.data.realMax ? customRound(Number(slice.data.realMax), 2) : 0}${t("usageChart.units.percentage")}`}
                </Typography>
              </div>
            )}
          />
        )}
      </Box>
    </div>
  )
}
